/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation, useQuery } from 'react-query'

import { deleteCluster, listClusters, patchCluster } from '@modules/ccm-api/v1/callers'
import type {
  Cluster,
  ClusterId,
  ClusterList,
  MultiErrorResponse,
  PatchClusterRequest,
} from '@modules/ccm-api/v1/types'
import { queryClient } from '@modules/query'

const CCM_LIST_CLUSTER_QUERY_KEY = 'ccmClusterList'

export const useGetCCMClusters = () =>
  useQuery<ClusterList, MultiErrorResponse>(CCM_LIST_CLUSTER_QUERY_KEY, () => listClusters())

export const usePatchCCMCluster = () =>
  useMutation<
    Cluster,
    MultiErrorResponse,
    {
      id: ClusterId
      body: PatchClusterRequest
    }
  >(
    ({ id, body }) =>
      patchCluster({
        pathParameters: {
          id,
        },
        body,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CCM_LIST_CLUSTER_QUERY_KEY)
      },
    },
  )

export const useDeleteCCMCluster = () =>
  useMutation<
    void,
    MultiErrorResponse,
    {
      id: ClusterId
    }
  >(
    ({ id }) =>
      deleteCluster({
        pathParameters: {
          id,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CCM_LIST_CLUSTER_QUERY_KEY)
      },
    },
  )
